import {
  EcommerceSettings,
  EcommerceSettingsService as EcommerceSettingsServiceSdk,
  IPurchaseFlowProperties,
  StoreMetaDataApi,
} from '@wix/wixstores-client-storefront-sdk';
import {PaymentMethodType} from '../../common/constants';
import {PaymentMethod} from '@wix/wixstores-graphql-schema-node';
import {getPayableCurrencies} from '@wix/ambassador-cashier-pay-v2-account/http';
import type {ControllerFlowAPI} from '@wix/yoshi-flow-editor';
import {SPECS} from '../specs';
import {ViewerContext} from '@wix/ecom-platform-storefront-common';

export class EcommerceSettingsService {
  private readonly ecommerceSettingsServiceSdk: EcommerceSettingsServiceSdk;
  private readonly purchaseFlowPropertiesPromise?: Promise<IPurchaseFlowProperties>;
  private ecommerceSettings?: EcommerceSettings;
  private readonly viewerContext: ViewerContext;
  private readonly flowAPI: ControllerFlowAPI;
  public hasCreatedPaymentMethods: boolean = true;

  constructor({
    ecommerceSettingsServiceSdk,
    flowAPI,
    viewerContext,
  }: {
    ecommerceSettingsServiceSdk: EcommerceSettingsServiceSdk;
    flowAPI: ControllerFlowAPI;
    viewerContext: ViewerContext;
  }) {
    this.ecommerceSettingsServiceSdk = ecommerceSettingsServiceSdk;
    this.flowAPI = flowAPI;
    this.viewerContext = viewerContext;
    if (!this.viewerContext.isExperimentEnabled(SPECS.StopUsingPaymentMethodsInCart)) {
      this.purchaseFlowPropertiesPromise = this.ecommerceSettingsServiceSdk.getPurchaseFlowProperties();
    }
  }

  public async get(): Promise<ReturnType<StoreMetaDataApi['getStoreMetaData']>> {
    /* istanbul ignore else */
    if (this.purchaseFlowPropertiesPromise !== undefined) {
      return (await this.purchaseFlowPropertiesPromise).storeMetaData;
    }
  }

  public async init() {
    try {
      this.ecommerceSettings = await this.ecommerceSettingsServiceSdk.getEcommerceSettings();
    } catch {
      this.ecommerceSettings = undefined;
    }
  }
  public async initPayableCurrencies() {
    try {
      const payableCurrencies =
        (
          await this.flowAPI.errorHandler.withErrorHandler(
            () => this.flowAPI.essentials.httpClient.request(getPayableCurrencies({})),
            {errorCodesMap: {}}
          )
        ).data.currencyCode ?? /* istanbul ignore next */ [];
      this.hasCreatedPaymentMethods = payableCurrencies.length > 0;
    } catch {
      this.hasCreatedPaymentMethods = true;
    }
  }

  public getEcommerceSettings() {
    return (
      this.ecommerceSettings ?? {
        hasValidationsInCart: true,
        hasAdditionalFees: true,
        tax: {taxIncludedInItemPrices: false},
      }
    );
  }

  private async getActivePaymentMethods() {
    const storeMetaData = await this.get();
    return storeMetaData?.activePaymentMethods ?? /* istanbul ignore next */ [];
  }

  private async getEWalletPaymentMethods(): Promise<PaymentMethod[]> {
    const activePaymentMethods = await this.getActivePaymentMethods();
    return activePaymentMethods.filter((paymentMethod) => paymentMethod.type === PaymentMethodType.eWallet);
  }

  private async getOfflinePaymentMethods(): Promise<PaymentMethod[]> {
    const activePaymentMethods = await this.getActivePaymentMethods();
    return activePaymentMethods.filter((paymentMethod) => paymentMethod.type === PaymentMethodType.offline);
  }

  public async hasEWalletPaymentMethods() {
    return !!(await this.getEWalletPaymentMethods()).length;
  }

  public async hasOfflinePaymentMethods() {
    return !!(await this.getOfflinePaymentMethods()).length;
  }

  public async getPaymentMethodsNames() {
    const activePaymentMethods = await this.getActivePaymentMethods();
    return activePaymentMethods.map((method) => method.name);
  }
}
